/**
 * PromocaoTab - Componente para gestão de promoções
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from '@/hooks/use-toast';

const PromocaoTab = ({ promocoes = [], produtos = [], cotacoes = [], onRefresh, usuario, showConfirmDialog }) => {
  const [showCriarModal, setShowCriarModal] = useState(false);
  const [showEditarModal, setShowEditarModal] = useState(false);
  const [showVisualizarModal, setShowVisualizarModal] = useState(false);
  const [promocaoSelecionada, setPromocaoSelecionada] = useState(null);
  const [nomePromocao, setNomePromocao] = useState('');
  const [produtosPromocao, setProdutosPromocao] = useState({});

  const temPermissaoComprador = () => {
    return usuario.tipo_usuario === 'Comprador';
  };

  // Função para obter custo com +5% da cotação mais recente (independente do status)
  const getCustoProduto = (produtoId, nomeVariacao = null) => {
    // Filtrar todas as cotações que contenham este produto (independente do status)
    const cotacoesComProduto = cotacoes
      .filter(c => c.produtos_ids && c.produtos_ids.includes(produtoId))
      .sort((a, b) => new Date(b.criado_em) - new Date(a.criado_em)); // Mais recente primeiro

    if (cotacoesComProduto.length === 0) {
      return 0; // Produto sem cotação - retorna 0 para permitir inclusão na promoção
    }

    const cotacaoMaisRecente = cotacoesComProduto[0];
    const produto = produtos.find(p => p.id === produtoId);
    let precoVencedor = 0;

    // PRIORIDADE 1: Verificar se há vencedor definido (cotação encerrada ou vencedor manual)
    const vencedor = cotacaoMaisRecente.vencedores?.[produtoId];
    
    // Se buscando variação específica e há vencedor com variação
    if (nomeVariacao && vencedor && vencedor.variacao === nomeVariacao) {
      if (produto && produto.unidade === 'UN') {
        precoVencedor = vencedor.preco_kg_un || vencedor.valor_caixa || 0;
      } else {
        precoVencedor = vencedor.quantidade_kg > 0 
          ? vencedor.valor_caixa / vencedor.quantidade_kg 
          : 0;
      }
    } else if (vencedor && !nomeVariacao) {
      // Há vencedor definido e não é busca por variação - usar esse preço
      if (produto && produto.unidade === 'UN') {
        precoVencedor = vencedor.preco_kg_un || vencedor.valor_caixa || 0;
      } else {
        precoVencedor = vencedor.quantidade_kg > 0 
          ? vencedor.valor_caixa / vencedor.quantidade_kg 
          : 0;
      }
    } else {
      // PRIORIDADE 2: Não há vencedor - buscar menor preço entre as propostas enviadas
      let menorPreco = null;
      
      Object.entries(cotacaoMaisRecente.fornecedores || {}).forEach(([fornecedorId, fornecedorData]) => {
        if (fornecedorData.enviado && fornecedorData.produtos && fornecedorData.produtos[produtoId]) {
          const oferta = fornecedorData.produtos[produtoId];
          
          // CORREÇÃO: Tratar produtos com VARIAÇÕES (inclui UN com variações)
          if (nomeVariacao && oferta.variacoes && oferta.variacoes[nomeVariacao]) {
            // Busca por variação específica
            const variacaoData = oferta.variacoes[nomeVariacao];
            if (variacaoData.valor_caixa && variacaoData.valor_caixa > 0) {
              const isUN = oferta.isUN || produto?.unidade === 'UN';
              const pesoCx = isUN ? 1 : (oferta.peso_cx || produto?.peso_cx || 1);
              const precoPorKg = variacaoData.valor_caixa / pesoCx;
              if (menorPreco === null || precoPorKg < menorPreco) {
                menorPreco = precoPorKg;
              }
            }
          } else if (!nomeVariacao) {
            // Busca geral (sem variação específica)
            let precoPorKg = null;
            
            // Verificar se tem variações (incluindo UN com variações via oferta.isUN)
            if ((produto && produto.variacoes && produto.variacoes.length > 0 && oferta.variacoes) || (oferta.isUN && oferta.variacoes)) {
              // Produto com variações: encontrar a variação com menor preço
              const isUN = oferta.isUN || produto?.unidade === 'UN';
              const pesoCx = isUN ? 1 : (oferta.peso_cx || produto?.peso_cx || 1);
              Object.entries(oferta.variacoes).forEach(([nomeVar, variacaoData]) => {
                if (variacaoData.valor_caixa && variacaoData.valor_caixa > 0) {
                  const valorPorKgVariacao = variacaoData.valor_caixa / pesoCx;
                  if (!precoPorKg || valorPorKgVariacao < precoPorKg) {
                    precoPorKg = valorPorKgVariacao;
                  }
                }
              });
            } else if (produto && produto.unidade === 'UN') {
              // Produto UN: usar preco_kg_un
              precoPorKg = oferta.preco_kg_un || null;
            } else {
              // Produto KG: calcular valor_caixa / quantidade_kg
              if (oferta.quantidade_kg && oferta.quantidade_kg > 0) {
                precoPorKg = oferta.valor_caixa / oferta.quantidade_kg;
              }
            }
            
            if (precoPorKg && precoPorKg > 0) {
              if (menorPreco === null || precoPorKg < menorPreco) {
                menorPreco = precoPorKg;
              }
            }
          }
        }
      });
      
      precoVencedor = menorPreco || 0;
    }

    // Retornar preço com +5%
    return precoVencedor * 1.05;
  };

  // Função para obter custos de todas as variações de um produto
  const getCustosVariacoes = (produtoId) => {
    const produto = produtos.find(p => p.id === produtoId);
    if (!produto || !produto.variacoes || produto.variacoes.length === 0) {
      return null;
    }
    
    const custosVariacoes = {};
    produto.variacoes.forEach(nomeVariacao => {
      custosVariacoes[nomeVariacao] = getCustoProduto(produtoId, nomeVariacao);
    });
    
    return custosVariacoes;
  };

  // Função para calcular margem
  const calcularMargem = (precoPromocao, custo) => {
    if (!precoPromocao || !custo || precoPromocao <= 0) return null;
    return ((precoPromocao - custo) / precoPromocao) * 100;
  };

  // Handler para criar promoção
  const handleCriarPromocao = async () => {
    if (!nomePromocao.trim()) {
      toast({
        title: "Nome obrigatório",
        description: "Por favor, informe o nome da promoção",
        variant: "destructive"
      });
      return;
    }

    try {
      await axios.post('/promocoes', { nome: nomePromocao });
      toast({
        title: "Promoção criada!",
        description: `Promoção "${nomePromocao}" criada com sucesso`
      });
      setNomePromocao('');
      setShowCriarModal(false);
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao criar promoção",
        description: error.response?.data?.detail || "Erro interno do servidor",
        variant: "destructive"
      });
    }
  };

  // Handler para abrir modal de edição
  const handleEditar = (promocao) => {
    setPromocaoSelecionada(promocao);
    
    // Inicializar estado com produtos da promoção
    const produtosMap = {};
    promocao.produtos.forEach(p => {
      produtosMap[p.produto_id] = {
        preco_promocao: p.preco_promocao || '',
        custo_kg_un: p.custo_kg_un,
        margem: p.margem || null
      };
    });
    setProdutosPromocao(produtosMap);
    setShowEditarModal(true);
  };

  // Handler para visualizar
  const handleVisualizar = (promocao) => {
    setPromocaoSelecionada(promocao);
    setShowVisualizarModal(true);
  };

  // Handler para excluir
  const handleExcluir = async (promocao) => {
    const confirmed = await showConfirmDialog(
      "Excluir Promoção",
      `Tem certeza que deseja excluir a promoção "${promocao.nome}"? Esta ação não poderá ser desfeita.`
    );
    
    if (!confirmed) return;

    try {
      await axios.delete(`/promocoes/${promocao.id}`);
      toast({
        title: "Promoção excluída!",
        description: "Promoção removida com sucesso"
      });
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao excluir",
        description: error.response?.data?.detail || "Erro interno do servidor",
        variant: "destructive"
      });
    }
  };

  // Handler para finalizar promoção
  const handleFinalizar = async () => {
    const confirmed = await showConfirmDialog(
      "Finalizar Promoção",
      "Deseja finalizar esta promoção? Os produtos serão salvos e você poderá editá-los posteriormente."
    );
    
    if (!confirmed) return;

    try {
      // Primeiro salvar os produtos antes de finalizar
      const produtosComPromocao = Object.entries(produtosPromocao)
        .filter(([_, data]) => data.preco_promocao && data.preco_promocao > 0)
        .map(([chave, data]) => {
          const produtoId = data.produto_id || chave.split('_')[0];
          const variacao = data.variacao || null;
          const custo = data.custo_kg_un || getCustoProduto(produtoId, variacao);
          return {
            produto_id: produtoId,
            variacao: variacao,
            custo_kg_un: custo,
            preco_promocao: parseFloat(data.preco_promocao),
            margem: calcularMargem(parseFloat(data.preco_promocao), custo)
          };
        });

      // Salvar produtos antes de finalizar
      await axios.put(`/promocoes/${promocaoSelecionada.id}`, {
        produtos: produtosComPromocao
      });

      // Então finalizar
      await axios.put(`/promocoes/${promocaoSelecionada.id}/finalizar`);
      toast({
        title: "Promoção finalizada!",
        description: "A promoção foi finalizada com sucesso"
      });
      setShowEditarModal(false);
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao finalizar",
        description: error.response?.data?.detail || "Erro interno do servidor",
        variant: "destructive"
      });
    }
  };

  // Render principal
  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Promoção</h3>
          <p className="text-sm text-gray-600">Gerencie suas promoções de produtos</p>
        </div>
        {temPermissaoComprador() && (
          <button
            onClick={() => setShowCriarModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800"
          >
            <span>➕</span>
            <span>Cadastrar Promoção</span>
          </button>
        )}
      </div>

      {/* Lista de Promoções */}
      {promocoes.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-4xl mb-4">🎯</div>
          <p className="text-gray-500">Nenhuma promoção criada ainda</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {promocoes.map(promocao => (
            <div key={promocao.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-lg">{promocao.nome}</h4>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  promocao.finalizada 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {promocao.finalizada ? 'Finalizada' : 'Em andamento'}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-1">
                Produtos: {promocao.produtos?.length || 0}
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Criada em: {new Date(promocao.criado_em).toLocaleDateString('pt-BR')}
              </p>
              <div className="flex space-x-2">
                {temPermissaoComprador() && (
                  <button
                    onClick={() => handleEditar(promocao)}
                    className="flex-1 px-3 py-2 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 text-sm font-medium"
                  >
                    EDITAR
                  </button>
                )}
                <button
                  onClick={() => handleVisualizar(promocao)}
                  className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm font-medium"
                >
                  VISUALIZAR
                </button>
                {temPermissaoComprador() && (
                  <button
                    onClick={() => handleExcluir(promocao)}
                    className="px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm font-medium"
                  >
                    EXCLUIR
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Criar Promoção */}
      {showCriarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-xl font-semibold">Criar Nova Promoção</h3>
              <button onClick={() => setShowCriarModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Nome da Promoção</label>
              <input
                type="text"
                value={nomePromocao}
                onChange={(e) => setNomePromocao(e.target.value)}
                placeholder="Ex: Promoção de Verão"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-2">
              <button
                onClick={() => setShowCriarModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Cancelar
              </button>
              <button
                onClick={handleCriarPromocao}
                className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800"
              >
                Criar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar - será continuado no próximo bloco devido ao tamanho */}
      {showEditarModal && promocaoSelecionada && (
        <ModalEditarPromocao 
          promocao={promocaoSelecionada}
          produtos={produtos}
          produtosPromocao={produtosPromocao}
          setProdutosPromocao={setProdutosPromocao}
          getCustoProduto={getCustoProduto}
          getCustosVariacoes={getCustosVariacoes}
          calcularMargem={calcularMargem}
          onClose={() => setShowEditarModal(false)}
          onFinalizar={handleFinalizar}
        />
      )}

      {/* Modal Visualizar */}
      {showVisualizarModal && promocaoSelecionada && (
        <ModalVisualizarPromocao 
          promocao={promocaoSelecionada}
          produtos={produtos}
          getCustoProduto={getCustoProduto}
          getCustosVariacoes={getCustosVariacoes}
          calcularMargem={calcularMargem}
          onClose={() => setShowVisualizarModal(false)}
        />
      )}
    </div>
  );
};

// Modal Editar Promoção
const ModalEditarPromocao = ({ promocao, produtos, produtosPromocao, setProdutosPromocao, getCustoProduto, getCustosVariacoes, calcularMargem, onClose, onFinalizar }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const handlePrecoChange = (produtoId, valor, variacao = null) => {
    const custo = getCustoProduto(produtoId, variacao);
    const precoFloat = parseFloat(valor);
    const margemCalculada = valor && precoFloat > 0 ? calcularMargem(precoFloat, custo) : null;
    
    const chave = variacao ? `${produtoId}_${variacao}` : produtoId;
    
    setProdutosPromocao(prev => ({
      ...prev,
      [chave]: {
        ...prev[chave],
        produto_id: produtoId,
        variacao: variacao,
        preco_promocao: valor,
        custo_kg_un: custo,
        margem: margemCalculada
      }
    }));
  };

  // Filtrar produtos baseado no termo de busca
  const produtosFiltrados = produtos.filter(produto => {
    if (!searchTerm) return true;
    return produto.nome.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Expandir lista para incluir variações
  const listaExpandida = [];
  produtosFiltrados.forEach(produto => {
    if (produto.variacoes && produto.variacoes.length > 0) {
      // Produto com variações: adicionar cada variação como linha separada
      produto.variacoes.forEach(nomeVariacao => {
        listaExpandida.push({
          produto: produto,
          variacao: nomeVariacao,
          chave: `${produto.id}_${nomeVariacao}`
        });
      });
    } else {
      // Produto sem variação
      listaExpandida.push({
        produto: produto,
        variacao: null,
        chave: produto.id
      });
    }
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h3 className="text-xl font-semibold">Editar Promoção - {promocao.nome}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
        </div>
        <div className="p-6">
          {/* Barra de pesquisa */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Pesquisar produto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <svg
                className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PRODUTO</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CUSTO (KG/UN)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PROMOÇÃO</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">MARGEM</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {listaExpandida.map(({ produto, variacao, chave }) => {
                  const custo = getCustoProduto(produto.id, variacao);
                  const precoPromocao = produtosPromocao[chave]?.preco_promocao;
                  // Calcular margem mesmo com custo 0 (permite produtos sem cotação)
                  const margem = precoPromocao && parseFloat(precoPromocao) > 0 ? calcularMargem(parseFloat(precoPromocao), custo) : null;

                  return (
                    <tr key={chave} className={variacao ? 'bg-purple-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {produto.nome}
                          {variacao && (
                            <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                              {variacao}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">{produto.unidade}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          R$ {custo.toFixed(2)}
                          {custo === 0 && <span className="ml-2 text-xs text-amber-600">(sem cotação)</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="number"
                          step="0.01"
                          value={precoPromocao || ''}
                          onChange={(e) => handlePrecoChange(produto.id, e.target.value, variacao)}
                          placeholder="0.00"
                          className="w-32 px-3 py-1 border border-gray-300 rounded-lg text-sm"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {margem !== null ? (
                          <span className={`text-sm font-medium ${margem >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {margem.toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {/* Mensagem quando não há resultados na busca */}
            {searchTerm && produtosFiltrados.length === 0 && (
              <div className="text-center py-8">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-gray-500 mt-2">Nenhum produto encontrado.</p>
                <p className="text-sm text-gray-400 mt-1">Tente outro termo de busca</p>
              </div>
            )}
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
          >
            Cancelar
          </button>
          <button
            onClick={onFinalizar}
            className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800"
          >
            Finalizar
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal Visualizar Promoção
const ModalVisualizarPromocao = ({ promocao, produtos, getCustoProduto, getCustosVariacoes, calcularMargem, onClose }) => {
  const produtosComPromocao = promocao.produtos.filter(p => p.preco_promocao && p.preco_promocao > 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h3 className="text-xl font-semibold">Visualizar Promoção - {promocao.nome}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
        </div>
        <div className="p-6">
          {!promocao.finalizada && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800 font-medium">⚠️ A promoção está em andamento</p>
            </div>
          )}
          
          {produtosComPromocao.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">📋</div>
              <p className="text-gray-500">Nenhum produto adicionado à promoção ainda</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PRODUTO</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CUSTO (KG/UN)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PROMOÇÃO</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">MARGEM</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {produtosComPromocao.map(prodPromocao => {
                    // Tentar encontrar produto diretamente pelo produto_id
                    let produto = produtos.find(p => p.id === prodPromocao.produto_id);
                    let variacao = prodPromocao.variacao || null;
                    
                    // Retrocompatibilidade: se não encontrou, tentar extrair do produto_id composto (formato antigo: "uuid_variacao")
                    if (!produto && prodPromocao.produto_id && prodPromocao.produto_id.includes('_')) {
                      const partes = prodPromocao.produto_id.split('_');
                      const uuidParte = partes[0];
                      const variacaoParte = partes.slice(1).join('_'); // Pegar resto como variação
                      produto = produtos.find(p => p.id === uuidParte);
                      if (produto && !variacao) {
                        variacao = variacaoParte;
                      }
                    }
                    
                    // Extrair produto_id limpo para custo
                    const produtoIdLimpo = produto?.id || prodPromocao.produto_id.split('_')[0];
                    
                    // Calcular custo atualizado em tempo real (com variação se houver)
                    const custoAtualizado = getCustoProduto(produtoIdLimpo, variacao);
                    
                    // Recalcular margem com base no custo atualizado
                    const margemAtualizada = calcularMargem(prodPromocao.preco_promocao, custoAtualizado);
                    
                    return (
                      <tr key={`${prodPromocao.produto_id}_${variacao || ''}`} className={variacao ? 'bg-purple-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {produto?.nome || 'Produto não encontrado'}
                            {variacao && (
                              <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                                {variacao}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">{produto?.unidade}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            R$ {custoAtualizado.toFixed(2)}
                            {custoAtualizado === 0 && <span className="ml-2 text-xs text-amber-600">(sem cotação)</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-green-600">
                            R$ {prodPromocao.preco_promocao.toFixed(2)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm font-medium ${margemAtualizada && margemAtualizada > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                            {margemAtualizada !== null ? `${margemAtualizada.toFixed(2)}%` : '-'}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromocaoTab;
